import { useCallback, useMemo } from 'react'

import { Alert } from '@vtex/shoreline'
import { t } from 'i18next'

import { formatMoneyV2, isPositiveNumber } from '~/helpers/format-number'
import {
  MIN_CAMPAIGN_PERIOD_DAYS,
  MIN_CAMPAIGN_PERIOD_WEEKS
} from '~/modules/retailMedia/constants/objectiveBased'

type AlertsProps = {
  dailyBudgetValues: {
    min: number | undefined
    recommended: number | undefined
  }
  minCostsError: Error | null
  dailyBudget: number | null
  period: CallbackDateInterval
}

export const Alerts = ({
  dailyBudgetValues,
  minCostsError,
  dailyBudget,
  period
}: AlertsProps) => {
  const shouldShowMinBudgetAlert = useMemo(() => {
    if (!isPositiveNumber(dailyBudgetValues?.min)) return false
    if (!isPositiveNumber(dailyBudget)) return false

    return dailyBudget < dailyBudgetValues.min
  }, [dailyBudget, dailyBudgetValues.min])

  const shouldShowDailyBudgetAlert = useMemo(() => {
    if (!isPositiveNumber(dailyBudgetValues.recommended)) return false
    if (!isPositiveNumber(dailyBudget)) return false

    return (
      dailyBudget >= dailyBudgetValues.min &&
      dailyBudget < dailyBudgetValues.recommended
    )
  }, [dailyBudget, dailyBudgetValues])

  const shouldShowPeriodAlert = useMemo(
    () =>
      // For always-on campaigns, period.interval is Infinity; in that case we do not show the short-period alert.
      Number.isFinite(period.interval) &&
      period.interval < MIN_CAMPAIGN_PERIOD_DAYS,
    [period]
  )

  const minBudgetAlertMessage = useCallback(() => {
    const formattedValue = formatMoneyV2({
      value: dailyBudgetValues.min
    })

    const key = 'rm:objectiveBased.setup.campaign.alert.minBudget'

    return t(key, { value: formattedValue })
  }, [dailyBudgetValues])

  const lowBudgetAlertMessage = useCallback(() => {
    const formattedValue = formatMoneyV2({
      value: dailyBudgetValues.recommended
    })

    const key = 'rm:objectiveBased.setup.campaign.alert.lowBudget'

    return t(key, { value: formattedValue })
  }, [dailyBudgetValues])

  const shortPeriodAlertMessage = useCallback(() => {
    const weeks = MIN_CAMPAIGN_PERIOD_WEEKS
    const days = MIN_CAMPAIGN_PERIOD_DAYS
    const key = 'rm:objectiveBased.setup.campaign.alert.shortPeriod'

    return t(key, { weeks, days })
  }, [])

  return (
    <>
      {shouldShowMinBudgetAlert && (
        <Alert variant="warning">{minBudgetAlertMessage()}</Alert>
      )}

      {shouldShowDailyBudgetAlert && (
        <Alert variant="warning">{lowBudgetAlertMessage()}</Alert>
      )}

      {shouldShowPeriodAlert && (
        <Alert variant="warning">{shortPeriodAlertMessage()}</Alert>
      )}

      {!!minCostsError && (
        <Alert variant="critical">
          {t('rm:objectiveBased.setup.minCosts.error')}
        </Alert>
      )}
    </>
  )
}
